import React, { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureFlag, ProfileType } from '@tendium/prom-types';
import styles from './index.module.scss';
import { useApiTenders } from 'src/models/procurements/Tenders/hooks';
import { GenericSearchDropdown, MatchingProfilesList } from 'src/shared';
import classNames from 'classnames';
import { useLoadMp } from 'src/models/matchingProfiles/hooks';
import { toSupplierIds, toSupplierStrings } from 'src/models/procurements/Tenders/helpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarsFilter, faSparkles } from '@fortawesome/pro-light-svg-icons';
import { useFeatureFlag } from 'src/helpers';

export const FilterProfiles: FC = () => {
  const { t } = useTranslation();
  const [{ vars }, updateVars] = useApiTenders();
  const { data: mp, loading } = useLoadMp(vars.profileId);

  const isExpiringContractsPageAvailable = useFeatureFlag(FeatureFlag.AnalyticsExpiringContracts);

  const onClick = useCallback(
    (profileId: string) => {
      updateVars({ profileId });
    },
    [updateVars]
  );

  useEffect(() => {
    if (vars.profileId && mp) {
      const supplierOrgIds = toSupplierIds(mp?.supplierSearchTerms);
      const supplierStrings = toSupplierStrings(mp?.supplierSearchTerms);

      updateVars({
        cpvCodesExact: !!mp?.cpvCodesExact?.length ? mp.cpvCodesExact : undefined,
        cpvCodes: !!mp?.cpvCodes?.length ? mp.cpvCodes : undefined,
        nutsCodesExact: !!mp?.nutsCodesExact?.length ? mp.nutsCodesExact : undefined,
        nutsCodes: !!mp?.nutsCodes?.length ? mp.nutsCodes : undefined,
        buyerOrgIds: !!mp?.buyerOrgIds?.length ? mp.buyerOrgIds : undefined,
        buyerSearchStrings: !!mp?.buyerSearchStrings?.length ? mp.buyerSearchStrings : undefined,
        procuringAgencyTypes: !!mp?.buyerTypes?.length ? mp.buyerTypes : undefined,
        supplierOrgIds: !!supplierOrgIds?.length ? supplierOrgIds : undefined,
        supplierSearchStrings: !!supplierStrings?.length ? supplierStrings : undefined,
        supplierSearchLogic: mp?.supplierSearchLogic,
        contractValueRange:
          mp?.contractValueCurrency || mp?.contractValueMax || mp?.contractValueMin
            ? {
                currency: mp?.contractValueCurrency,
                max: mp?.contractValueMax,
                min: mp?.contractValueMin
              }
            : undefined,
        keywordArgs:
          mp?.keywords?.map(keyword => ({
            value: keyword.value,
            searchType: keyword.searchType,
            searchLogic: keyword.searchLogic,
            filterLogic: keyword.filterLogic,
            highlightColor: keyword.highlightColor
          })) ?? undefined,
        contractValueIncludeMissingValue: mp?.contractValueIncludeMissingValue
      });
    }
  }, [mp, updateVars, vars.profileId]);

  return (
    <GenericSearchDropdown
      overlayClassName={styles.overlay}
      buttonClassName={classNames({ [styles.button]: mp?.profileType === ProfileType.Matching })}
      isChanged={!!vars?.profileId}
      label={
        vars.profileId && mp ? (
          <span className={styles.label}>
            <FontAwesomeIcon
              className={styles.labelIcon}
              icon={mp.profileType === ProfileType.ExpiringContract ? faBarsFilter : faSparkles}
            />
            {mp.name}
          </span>
        ) : (
          t('ExpiringContracts.savedSearch')
        )
      }
      loading={loading}
      overlay={
        <>
          {isExpiringContractsPageAvailable && (
            <MatchingProfilesList
              activeId={vars.profileId}
              type={ProfileType.ExpiringContract}
              onClick={onClick}
              className={styles.list}
              title={t('ExpiringContracts.savedSearch')}
            />
          )}
          <MatchingProfilesList
            activeId={vars.profileId}
            type={ProfileType.Matching}
            onClick={onClick}
            className={styles.list}
            title={t('Tenders.monitoringProfiles')}
          />
        </>
      }
    />
  );
};

export default FilterProfiles;
